import React, { useState, useEffect } from 'react';
import { FaMagic, FaSpinner, FaCheck, FaExclamationCircle, FaDownload, FaTimes, FaWordpress } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../apis/axiosConfig';
import { useDarkMode } from '../hooks/useDarkMode';

const ImageOptimizerSection = ({ projectId, pageUrl }) => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  
  const [processingId, setProcessingId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(50);

  // WordPress dialog state
  const [showWordPressDialog, setShowWordPressDialog] = useState(false);

  useEffect(() => {
    const fetchPluginConnection = async () => {
      if (!projectId) return;
      try {
        const response = await axios.get('/auto-implementation/plugin-connection/check/', {
          params: { project_id: projectId }
        });
        
        if (response.data.is_connected && response.data.site_url && !response.data.has_incomplete_connection) {
          setIsConnected(true);
        } else {
          setIsConnected(false);
        }
      } catch (err) {
        setIsConnected(false);
      } finally {
        setLoadingInitial(false);
      }
    };
    
    fetchPluginConnection();
  }, [projectId]);

  const handleScanClick = () => {
    if (!isConnected) {
      // Show dialog asking if it's a WordPress site
      setShowWordPressDialog(true);
    } else {
      handleScan();
    }
  };

  const handleWordPressYes = () => {
    setShowWordPressDialog(false);
    // Redirect to plugin connection page, passing return path
    navigate('/dashboard/plugin-connection', {
      state: {
        projectId,
        returnTo: location.pathname + location.search,
      }
    });
  };

  const handleWordPressNo = () => {
    setShowWordPressDialog(false);
    // Proceed with scan without WordPress plugin
    handleScan();
  };

  const handleScan = async () => {
    if (!projectId || !pageUrl) {
      toast.error('Project ID or Page URL is missing.');
      return;
    }
    
    setScanning(true);
    setScanResults(null);
    try {
      const firebaseUid = localStorage.getItem('firebase_uid') || '';
      const response = await axios.post('/image-optimizer/scan/', {
        project_id: projectId,
        page_url: pageUrl,
        firebase_uid: firebaseUid
      });
      setScanResults(response.data.images || []);
      if (response.data.images?.length === 0) {
        toast.info("No unoptimized images found on this page.");
      } else {
        toast.success("Image scan completed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to scan images.");
    } finally {
      setScanning(false);
    }
  };

  const handleReplace = async (image) => {
    if (!isConnected) {
      setShowWordPressDialog(true);
      return;
    }
    setProcessingId(`replace-${image.attachment_id}`);
    try {
      const firebaseUid = localStorage.getItem('firebase_uid') || '';
      await axios.post('/image-optimizer/replace/', {
        project_id: projectId,
        attachment_id: image.attachment_id,
        firebase_uid: firebaseUid
      });
      toast.success("Image replacement queued! WordPress will apply it shortly.");
      
      setScanResults(prev => prev.map(img => 
        img.attachment_id === image.attachment_id 
          ? { ...img, action: 'Replaced on Site', handled: true } 
          : img
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to queue image replacement.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRollback = async (image) => {
    setProcessingId(`rollback-${image.attachment_id}`);
    try {
      const firebaseUid = localStorage.getItem('firebase_uid') || '';
      await axios.post('/image-optimizer/rollback/', {
        project_id: projectId,
        attachment_id: image.attachment_id,
        firebase_uid: firebaseUid
      });
      toast.success("Image successfully rolled back to original.");
      
      setScanResults(prev => prev.map(img => 
        img.attachment_id === image.attachment_id 
          ? { ...img, action: 'Rolled Back', handled: true } 
          : img
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rollback image.");
    } finally {
      setProcessingId(null);
    }
  };

  if (loadingInitial) {
    return (
      <div className={`p-6 rounded-lg shadow-lg mt-6 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <FaSpinner className="animate-spin text-[#008300] text-3xl mx-auto mb-4" />
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Checking connection...</p>
      </div>
    );
  }

  return (
    <>
      {/* WordPress Connection Dialog */}
      {showWordPressDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-100'}`}>
            {/* Close button */}
            <button
              onClick={() => setShowWordPressDialog(false)}
              className={`absolute top-4 right-4 p-1.5 rounded-lg transition ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <FaTimes />
            </button>

            {/* Content */}
            <div className="p-8 text-center">
              <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-5" style={{ backgroundColor: 'rgba(0,131,0,0.15)' }}>
                <FaWordpress className="text-3xl" style={{ color: '#008300' }} />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Is this a WordPress site?
              </h3>
              <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                To automatically update your site's images, you need to connect the AI SEO Tool WordPress plugin. Would you like to set it up now?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleWordPressNo}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  No, skip
                </button>
                <button
                  onClick={handleWordPressYes}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-medium transition flex items-center justify-center gap-2" style={{ backgroundColor: '#008300' }} onMouseEnter={e => e.currentTarget.style.backgroundColor='#006600'} onMouseLeave={e => e.currentTarget.style.backgroundColor='#008300'}
                >
                  <FaWordpress /> Yes, connect plugin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`p-6 rounded-lg shadow-lg mt-6 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-100'}`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className={`text-xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <FaMagic className="text-[#008300]" /> Image Optimizer
            </h3>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Automatically detect, compress, and update images on your site.
            </p>
            {!isConnected && (
              <p className={`text-xs mt-1.5 flex items-center gap-1.5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                <FaExclamationCircle />
                WordPress plugin not connected.
              </p>
            )}
          </div>
          <button
            onClick={handleScanClick}
            disabled={scanning}
            className={`mt-4 sm:mt-0 px-6 py-2.5 rounded-lg font-medium tracking-wide flex items-center gap-2 transition duration-200 
              ${scanning 
                ? 'bg-gray-400 cursor-not-allowed text-white' 
                : 'bg-[#008300] hover:bg-[#006600] text-white shadow-lg shadow-green-500/30'}`}
          >
            {scanning ? <FaSpinner className="animate-spin" /> : <FaMagic />}
            {scanning ? 'Scanning & Compressing...' : 'Scan & Optimize Images'}
          </button>
        </div>

        {scanResults && (
          <div className="space-y-4">
            {scanResults.length === 0 ? (
              <div className={`text-center py-8 rounded-lg border border-dashed ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-300 bg-gray-50'}`}>
                <FaCheck className="text-[#008300] text-4xl mx-auto mb-3" />
                <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>All images on this page are already optimized!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {scanResults.map((img, idx) => (
                  <div key={idx} className={`rounded-xl overflow-hidden border transition-all duration-300 hover:shadow-xl ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                    {/* Image Preview Area */}
                    <div 
                      className={`flex relative h-48 bg-gray-100 dark:bg-gray-800 overflow-hidden group ${img.optimized ? 'cursor-pointer' : ''}`}
                      onClick={() => img.optimized && setSelectedImage(img)}
                    >
                      {/* Before Image */}
                      <div className="flex-1 relative border-r border-white/20">
                        <img 
                          src={img.original_url} 
                          alt={`Original ${img.filename}`} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-1 font-semibold uppercase tracking-wider backdrop-blur-sm">
                          Before
                        </div>
                      </div>

                      {/* After Image */}
                      <div className="flex-1 relative">
                        <img 
                          src={img.preview_b64} 
                          alt={`Compressed ${img.filename}`} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-1 font-semibold uppercase tracking-wider backdrop-blur-sm text-[#008300]">
                          After
                        </div>
                        {img.optimized && (
                          <div className="absolute top-2 right-2 bg-[#008300] text-white text-xs font-bold px-2 py-0.5 rounded shadow-lg backdrop-blur-md bg-opacity-90 z-10">
                            -{img.savings_percent}%
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details Area */}
                    <div className="p-4">
                      <p className={`text-sm font-semibold truncate mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`} title={img.filename}>
                        {img.filename}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs mb-3">
                        <div className="text-center">
                          <p className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>Original</p>
                          <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{img.original_size_human}</p>
                        </div>
                        <div className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                          <span className="text-gray-400">→</span>
                        </div>
                        <div className="text-center">
                          <p className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>Compressed</p>
                          <p className={`font-medium ${img.optimized ? 'text-[#008300]' : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                            {img.compressed_size_human}
                          </p>
                        </div>
                      </div>

                      {img.optimized && (
                        <div className={`flex items-center justify-between text-xs mb-4 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                          <span className={`font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Savings:</span>
                          <div className={`text-right font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                            -{img.savings_percent}%
                            <span className="ml-1 opacity-80 font-normal">
                              ({((img.original_size - img.compressed_size) / 1024).toFixed(2)}KB)
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-2">
                         {img.optimized ? (
                          <>
                            <div className="flex gap-2 max-sm:flex-col">
                              {/* Auto Update / Connect Plugin Button */}
                              <button
                                onClick={() => {
                                  if (!(img.handled && img.action === 'Replaced on Site')) {
                                    handleReplace(img);
                                  }
                                }}
                                disabled={processingId !== null || (img.handled && img.action === 'Replaced on Site')}
                                className={`flex-1 py-1.5 rounded text-sm font-medium transition flex justify-center items-center gap-2
                                  ${img.handled && img.action === 'Replaced on Site' 
                                    ? 'bg-[#008300] text-white opacity-90 cursor-default' 
                                    : isConnected
                                      ? 'bg-[#008300] hover:bg-[#006600] text-white disabled:opacity-50'
                                      : 'bg-[#008300] hover:bg-[#006600] text-white disabled:opacity-50'}`}
                              >
                                {processingId === `replace-${img.attachment_id}` 
                                  ? <FaSpinner className="animate-spin" /> 
                                  : img.handled && img.action === 'Replaced on Site' 
                                    ? 'Updates Live!' 
                                    : isConnected ? 'Auto Update' : '🔌 Connect & Update'
                                }
                              </button>
                              
                              {/* Rollback Button */}
                              <button
                                onClick={() => handleRollback(img)}
                                disabled={processingId !== null || !(img.handled && img.action === 'Replaced on Site')}
                                className={`flex-1 py-1.5 rounded text-sm font-medium transition flex justify-center items-center gap-2 
                                  ${!(img.handled && img.action === 'Replaced on Site') || processingId !== null
                                    ? isDarkMode ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                              >
                                {processingId === `rollback-${img.attachment_id}` ? <FaSpinner className="animate-spin" /> : 'Rollback'}
                              </button>
                            </div>
                            
                            {/* Download Button */}
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = img.preview_b64;
                                link.download = `optimized_${img.filename}`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className={`w-full py-1.5 rounded text-sm font-medium transition flex justify-center items-center gap-2 
                                ${isDarkMode ? 'bg-green-900/40 hover:bg-green-900/60 text-green-300' : 'bg-[#004d00] hover:bg-[#003300] text-white'}`}
                            >
                              <FaDownload /> Download
                            </button>
                          </>
                        ) : (
                          <div className="flex gap-2">
                            <div className={`flex-1 py-2 text-center text-sm rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                              Already Optimal
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* High-Res Comparison Modal */}
        {selectedImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className={`relative w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}>
              {/* Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>High-Res Image Comparison</h3>
                <button 
                  onClick={() => {
                    setSelectedImage(null);
                    setSliderPosition(50);
                  }}
                  className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                >
                  <FaTimes size={20} />
                </button>
              </div>
              
              {/* Content - Interactive Slider */}
              <div className="p-6">
                <div className="relative w-full max-w-4xl mx-auto rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700 shadow-lg bg-gray-100 dark:bg-gray-800" style={{ minHeight: '50vh', maxHeight: '70vh' }}>
                  <div className="absolute top-0 left-0 right-0 z-20 flex justify-between p-4 pointer-events-none">
                    <div className={`px-3 py-1.5 rounded-lg font-semibold text-sm shadow-md ${isDarkMode ? 'bg-gray-800/80 text-white' : 'bg-white/80 text-gray-900'} backdrop-blur-md`}>
                      BEFORE: <span className="opacity-80">{selectedImage.original_size_human}</span>
                    </div>
                    <div className={`px-3 py-1.5 rounded-lg font-semibold text-sm shadow-md ${isDarkMode ? 'bg-green-900/80 text-green-100' : 'bg-green-100/80 text-green-800'} backdrop-blur-md`}>
                      AFTER: <span className="opacity-80">{selectedImage.compressed_size_human}</span>
                    </div>
                  </div>

                  {/* Before Image (Base) */}
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <img 
                      src={selectedImage.original_url} 
                      alt="Original High-Res" 
                      className="max-w-full max-h-full object-contain select-none"
                      draggable="false"
                    />
                  </div>
                  
                  {/* After Image (Clipped) */}
                  <div 
                    className="absolute inset-0 flex items-center justify-center p-4"
                    style={{ clipPath: `polygon(${sliderPosition}% 0, 100% 0, 100% 100%, ${sliderPosition}% 100%)` }}
                  >
                    <img 
                      src={selectedImage.preview_b64} 
                      alt="Optimized High-Res" 
                      className="max-w-full max-h-full object-contain select-none"
                      draggable="false"
                    />
                  </div>

                  {/* Slider Handle Line */}
                  <div 
                    className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-[0_0_10px_rgba(0,0,0,0.5)] z-20 pointer-events-none flex items-center justify-center"
                    style={{ left: `calc(${sliderPosition}% - 2px)` }}
                  >
                    <div className="w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-gray-300">
                      <div className="flex gap-1">
                        <div className="w-0.5 h-3 bg-gray-400 rounded-full"></div>
                        <div className="w-0.5 h-3 bg-gray-400 rounded-full"></div>
                        <div className="w-0.5 h-3 bg-gray-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  {/* Invisible Range Input for Dragging */}
                  <input 
                    type="range" 
                    min="0" 
                    max="100" 
                    value={sliderPosition} 
                    onChange={(e) => setSliderPosition(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30 m-0"
                  />
                </div>
                <div className="text-center mt-4 text-sm text-gray-500 dark:text-gray-400">
                  Drag the slider left and right to compare image quality.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ImageOptimizerSection;
